import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  Chip,
  LinearProgress,
  Alert,
  Paper,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import GenericTable from "../../components/shared/genericTable/genericTable";
import JiraUserDetailsModal from "../../components/modals/jiraUserDetailsModal";
import JiraPerformanceTrend from "../../components/jira/jiraPerformanceTrend/jiraPerformanceTrend";
import JiraTicketDetailsModal from "../../components/modals/jiraTicketDetailsModal";
import SprintTicketsModal from "../../components/modals/sprintTicketsModal";
import { callAPI } from "../../functions/api";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import JiraProductivity from "../../components/jira/jiraProductivity/jiraProductivity";
import { useUser } from "../../functions/userContext";
import { useTheme } from "@mui/material/styles";
import { devError } from "../../functions/logging";
import { Tabs } from "../../components/shared/tabs/tabs";
import { JiraViweSideBar } from "./jiraView.sideBar";
import BarChartGeneric from "../../components/shared/barChart/barChart";
import LineGraph from "../../components/shared/lineGraph/lineGraph";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import BlockIcon from "@mui/icons-material/Block";

dayjs.extend(advancedFormat);

const JiraView = () => {
  const [jiraTickets, setJiraTickets] = useState([]);
  const [assigndData, setAssignedData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [teamTrendData, setTeamTrendData] = useState([]);
  const [teamMetric, setTeamMetric] = useState("storyPoints");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [sprintModalOpen, setSprintModalOpen] = useState(false);
  const [selectedSprintTickets, setSelectedSprintTickets] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("CloudOps");
  const [sprintTickets, setSprintTickets] = useState([]);
  const [selectedSprintName, setSelectedSprintName] = useState("");
  const [boards, setBoards] = useState([]);
  const [ticketAging, setTicketAging] = useState(null);
  const [loadingTicketAging, setLoadingTicketAging] = useState(true);
  const [oldestOpenTickets, setOldestTickets] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "Sprint",
    direction: "accending",
    isCustom: true,
    scoreFn: (row) => row.sprintName,
  });
  const { setShowTabs, showTabs, setCurrentTab } = useUser();
  const [activeTab, setActiveTab] = useState(0);
  const tabTitles = [
    "CloudOps Jira",
    "Sprint Health",
    "Quality & SLA",
    "Bottlenecks & Capacity",
    "Workload & Balance",
    "Critical Items",
  ];
  const [loadingJiraTickets, setLoadingJiraTickets] = useState(true);

  useEffect(() => {
    if (tabTitles[activeTab]) {
      setCurrentTab(tabTitles[activeTab]);
    }
    return () => setCurrentTab(null);
  }, [activeTab, setCurrentTab]);
  const [loadingProductivity, setLoadingProductivity] = useState(true);
  const theme = useTheme();

  // New state for enhanced features
  const [sprintHealth, setSprintHealth] = useState([]);
  const [loadingSprintHealth, setLoadingSprintHealth] = useState(true);
  const [slaCompliance, setSlaCompliance] = useState({ overallCompliance: 0, breachedItems: [] });
  const [loadingSlaCompliance, setLoadingSlaCompliance] = useState(true);
  const [qualityMetrics, setQualityMetrics] = useState({
    summary: { bugCreationTrend: 0, bugsCreatedLast30Days: 0, bugsResolvedLast30Days: 0 },
    weeklyTrend: [],
    agingBuckets: { fresh: 0, week: 0, month: 0, old: 0 },
  });
  const [loadingQualityMetrics, setLoadingQualityMetrics] = useState(true);
  const [bottleneckAnalysis, setBottleneckAnalysis] = useState({ byIssueType: [] });
  const [loadingBottleneckAnalysis, setLoadingBottleneckAnalysis] = useState(true);
  const [workloadBalance, setWorkloadBalance] = useState({
    summary: {
      balanceStatus: "Unknown",
      fairnessScore: 0,
      avgTicketsPerPerson: 0,
      overloadedCount: 0,
      balancedCount: 0,
    },
    workload: [],
  });
  const [loadingWorkloadBalance, setLoadingWorkloadBalance] = useState(true);
  const [capacityForecast, setCapacityForecast] = useState({
    historicalVelocity: { avgPoints: 0, trend: [] },
    backlog: { totalItems: 0, totalPoints: 0, estimatedSprintsToComplete: 0 },
    forecast: [],
  });
  const [loadingCapacityForecast, setLoadingCapacityForecast] = useState(true);
  const [blockedItems, setBlockedItems] = useState({
    summary: { totalBlocked: 0, criticalBlocked: 0, avgDaysBlocked: 0, longestBlocked: 0 },
    items: [],
  });
  const [loadingBlockedItems, setLoadingBlockedItems] = useState(true);
  const [criticalAging, setCriticalAging] = useState({
    summary: { totalCritical: 0, highPriorityAging: 0, agingBugs: 0, openIncidents: 0 },
    items: [],
  });
  const [loadingCriticalAging, setLoadingCriticalAging] = useState(true);
  const [teamComparison, setTeamComparison] = useState({
    averages: { avgCycleTime: 0, avgVelocity: 0, avgTicketsCompleted: 0 },
    teams: [],
  });
  const [loadingTeamComparison, setLoadingTeamComparison] = useState(true);
  const [weeklySummary, setWeeklySummary] = useState([]);
  const [loadingWeeklySummary, setLoadingWeeklySummary] = useState(true);
  useEffect(() => {
    setShowTabs(false);

    let currentDepartmentString =
      selectedDepartment == "ALL" ? "" : `?department=${selectedDepartment}`;

    callAPI(`ticketing/jira/tickets/active${currentDepartmentString}`, "GET", null, true)
      .then((data) => setJiraTickets(data?.value || data || []))
      .catch((error) => {
        devError("Error fetching active tickets", error);
      })
      .finally(() => {
        setLoadingJiraTickets(false);
      });

    callAPI(`ticketing/jira/team-performance-trends${currentDepartmentString}`, "GET", null, true)
      .then((data) => setTeamTrendData(data?.value || data || []))
      .catch((error) => {
        devError("Error fetching active tickets", error);
      })
      .finally(() => {});

    callAPI(`ticketing/jira/assignee-productivity${currentDepartmentString}`, "GET", null, true)
      .then((data) => {
        setAssignedData(data?.value || data || []);
      })
      .catch((error) => {
        devError("Error fetching active tickets", error);
      })
      .finally(() => {
        setLoadingProductivity(false);
      });

    callAPI(`ticketing/jira/boards`, "GET", null, true).then((data) => {
      setBoards(data?.value || data || []);
    });

    callAPI("ticketing/jira/tickets/aging", "GET", null, true)
      .then((data) => {
        setTicketAging(data);
      })
      .then(() => {
        setLoadingTicketAging(false);
      });

    callAPI("ticketing/jira/tickets/oldest", "GET", null, true).then((data) => {
      setOldestTickets(data);
    });

    // Fetch new enhanced data
    callAPI(`ticketing/jira/sprint-health${currentDepartmentString}`, "GET", null, true)
      .then((data) => setSprintHealth(data?.value || data || []))
      .catch((error) => devError("Error fetching sprint health", error))
      .finally(() => setLoadingSprintHealth(false));

    callAPI(`ticketing/jira/sla-compliance${currentDepartmentString}`, "GET", null, true)
      .then((data) =>
        setSlaCompliance(data?.value || data || { overallCompliance: 0, breachedItems: [] })
      )
      .catch((error) => {
        devError("Error fetching SLA compliance", error);
        setSlaCompliance({ overallCompliance: 0, breachedItems: [] });
      })
      .finally(() => setLoadingSlaCompliance(false));

    callAPI(`ticketing/jira/quality-metrics${currentDepartmentString}`, "GET", null, true)
      .then((data) =>
        setQualityMetrics(
          data?.value ||
            data || {
              summary: { bugCreationTrend: 0, bugsCreatedLast30Days: 0, bugsResolvedLast30Days: 0 },
              weeklyTrend: [],
              agingBuckets: { fresh: 0, week: 0, month: 0, old: 0 },
            }
        )
      )
      .catch((error) => {
        devError("Error fetching quality metrics", error);
        setQualityMetrics({
          summary: { bugCreationTrend: 0, bugsCreatedLast30Days: 0, bugsResolvedLast30Days: 0 },
          weeklyTrend: [],
          agingBuckets: { fresh: 0, week: 0, month: 0, old: 0 },
        });
      })
      .finally(() => setLoadingQualityMetrics(false));

    callAPI(`ticketing/jira/bottleneck-analysis${currentDepartmentString}`, "GET", null, true)
      .then((data) => setBottleneckAnalysis(data?.value || data || { byIssueType: [] }))
      .catch((error) => {
        devError("Error fetching bottleneck analysis", error);
        setBottleneckAnalysis({ byIssueType: [] });
      })
      .finally(() => setLoadingBottleneckAnalysis(false));

    callAPI(`ticketing/jira/workload-balance${currentDepartmentString}`, "GET", null, true)
      .then((data) =>
        setWorkloadBalance(
          data?.value ||
            data || {
              summary: {
                balanceStatus: "Unknown",
                fairnessScore: 0,
                avgTicketsPerPerson: 0,
                overloadedCount: 0,
                balancedCount: 0,
              },
              workload: [],
            }
        )
      )
      .catch((error) => {
        devError("Error fetching workload balance", error);
        setWorkloadBalance({
          summary: {
            balanceStatus: "Unknown",
            fairnessScore: 0,
            avgTicketsPerPerson: 0,
            overloadedCount: 0,
            balancedCount: 0,
          },
          workload: [],
        });
      })
      .finally(() => setLoadingWorkloadBalance(false));

    callAPI(`ticketing/jira/capacity-forecast`, "GET", null, true)
      .then((data) =>
        setCapacityForecast(
          data?.value ||
            data || {
              historicalVelocity: { avgPoints: 0, trend: [] },
              backlog: { totalItems: 0, totalPoints: 0, estimatedSprintsToComplete: 0 },
              forecast: [],
            }
        )
      )
      .catch((error) => {
        devError("Error fetching capacity forecast", error);
        setCapacityForecast({
          historicalVelocity: { avgPoints: 0, trend: [] },
          backlog: { totalItems: 0, totalPoints: 0, estimatedSprintsToComplete: 0 },
          forecast: [],
        });
      })
      .finally(() => setLoadingCapacityForecast(false));

    callAPI(`ticketing/jira/blocked-items${currentDepartmentString}`, "GET", null, true)
      .then((data) =>
        setBlockedItems(
          data?.value ||
            data || {
              summary: {
                totalBlocked: 0,
                criticalBlocked: 0,
                avgDaysBlocked: 0,
                longestBlocked: 0,
              },
              items: [],
            }
        )
      )
      .catch((error) => {
        devError("Error fetching blocked items", error);
        setBlockedItems({
          summary: { totalBlocked: 0, criticalBlocked: 0, avgDaysBlocked: 0, longestBlocked: 0 },
          items: [],
        });
      })
      .finally(() => setLoadingBlockedItems(false));

    callAPI(`ticketing/jira/critical-aging${currentDepartmentString}`, "GET", null, true)
      .then((data) =>
        setCriticalAging(
          data?.value ||
            data || {
              summary: { totalCritical: 0, highPriorityAging: 0, agingBugs: 0, openIncidents: 0 },
              items: [],
            }
        )
      )
      .catch((error) => {
        devError("Error fetching critical aging", error);
        setCriticalAging({
          summary: { totalCritical: 0, highPriorityAging: 0, agingBugs: 0, openIncidents: 0 },
          items: [],
        });
      })
      .finally(() => setLoadingCriticalAging(false));

    callAPI("ticketing/jira/team-comparison", "GET", null, true)
      .then((data) =>
        setTeamComparison(
          data?.value ||
            data || {
              averages: { avgCycleTime: 0, avgVelocity: 0, avgTicketsCompleted: 0 },
              teams: [],
            }
        )
      )
      .catch((error) => {
        devError("Error fetching team comparison", error);
        setTeamComparison({
          averages: { avgCycleTime: 0, avgVelocity: 0, avgTicketsCompleted: 0 },
          teams: [],
        });
      })
      .finally(() => setLoadingTeamComparison(false));

    callAPI(`ticketing/jira/team-weekly-summary${currentDepartmentString}`, "GET", null, true)
      .then((data) => setWeeklySummary(data?.value || data || []))
      .catch((error) => devError("Error fetching weekly summary", error))
      .finally(() => setLoadingWeeklySummary(false));
  }, [selectedDepartment]);

  const openUserModal = async (assigneeName) => {
    const fullData = await callAPI(
      `ticketing/jira/user-details?username=${assigneeName}`,
      "GET",
      null,
      true
    );
    setSelectedUser(fullData);
    setUserModalOpen(true);
  };

  // Calculate overall loading state
  const isInitialLoading =
    loadingJiraTickets ||
    loadingSprintHealth ||
    loadingSlaCompliance ||
    loadingQualityMetrics ||
    loadingBottleneckAnalysis ||
    loadingCapacityForecast ||
    loadingBlockedItems ||
    loadingCriticalAging;

  const handleSprintClick = async (sprintName) => {
    if (!sprintName) return;

    if (sprintName.toLowerCase() === "bau") {
      sprintName = "";
    }

    const matchedTickets = await await callAPI(
      `ticketing/jira/tickets-for-sprint?sprintName=${sprintName}`,
      "GET",
      null,
      true
    );

    setSelectedSprintTickets(matchedTickets);
    setSelectedSprintName(sprintName);
    setSprintModalOpen(true);
  };

  const getSprintColor = (sprintName) => {
    let hash = 0;
    for (let i = 0; i < sprintName.length; i++) {
      hash = sprintName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360; // Unique hue for each sprint
    return `hsl(${hue}, 70%, 50%)`; // Bright, saturated
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "in progress":
        return "#FFE082"; // warm pastel yellow
      case "done":
        return "#A5D6A7"; // brighter soft green
      case "blocked":
        return "#EF9A9A"; // lightened red
      case "to do":
        return "#2196f3"; // blue
      default:
        return "#CFD8DC"; // light grey fallback
    }
  };

  const handleStatusClick = (status) => {
    // or trigger a modal/filter
  };

  const getTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case "bug":
        return "#EF9A9A"; // soft coral red
      case "story":
        return "#90CAF9"; // soft blue
      case "task":
        return "#A5D6A7"; // gentle green
      case "epic":
        return "#CE93D8"; // soft violet
      default:
        return "#E0E0E0"; // neutral gray
    }
  };

  const handleTypeClick = (type) => {
    // add filter or modal logic here
  };

  const customColumns = [
    {
      name: "Age (days)",
      align: "center",
      score: (row) => {
        const ageInDays = row?.ageInDays || 0;
        return ageInDays;
      },
      render: (row) => {
        const ageInDays = row?.ageInDays || 0;
        return (
          <Box
            sx={{
              display: "inline-block",
              px: 1.2,
              py: 0.3,
              borderRadius: "999px",
              fontSize: "12px",
              fontWeight: "bold",

              color: ageInDays > 30 ? "#f44336" : theme.palette.text.primary, // red if age is 30 days or more
              cursor: "pointer",
              whiteSpace: "nowrap",
              maxWidth: "85px",
              width: "85px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "center",
            }}
            title={`${ageInDays} days old`}
          >
            {ageInDays} days
          </Box>
        );
      },
    },
    {
      name: "Type",
      align: "center",
      score: (row) => row?.issueType?.trim() || "Unknown",
      render: (row) => {
        const type = row?.issueType?.trim() || "Unknown";
        const color = getTypeColor(type); // Define this function

        return (
          <Box
            onClick={() => handleTypeClick(type)} // Optional
            sx={{
              display: "inline-block",
              px: 1.2,
              py: 0.3,
              borderRadius: "999px",
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: color,
              color: "#212121",
              cursor: "pointer",
              whiteSpace: "nowrap",
              maxWidth: "85px",
              width: "85px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "center",
            }}
            title={type}
          >
            {type}
          </Box>
        );
      },
    },
    {
      name: "Status",
      align: "center",
      score: (row) => row?.status?.trim() || "Unknown",
      render: (row) => {
        const status = row?.status?.trim() || "Unknown";
        const color = getStatusColor(status); // Define this function to return a color based on status

        return (
          <Box
            onClick={() => handleStatusClick(status)} // Optional handler if needed
            sx={{
              display: "inline-block",
              px: 1.2,
              py: 0.3,
              borderRadius: "999px",
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: color,
              color: "#212121",
              cursor: "pointer",
              whiteSpace: "nowrap",
              maxWidth: "85px",
              width: "85px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "center",
            }}
            title={status}
          >
            {status}
          </Box>
        );
      },
    },

    {
      name: "Sprint",
      align: "center",
      score: (row) => row?.sprintName?.trim() || "BAU",
      render: (row) => {
        const sprint = row?.sprintName?.trim() || "BAU";
        const color = getSprintColor(sprint);

        return (
          <Box
            onClick={() => handleSprintClick(sprint)}
            sx={{
              display: "inline-block",
              px: 1.2,
              py: 0.3,
              borderRadius: "999px",
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: color,
              color: "#fff",
              cursor: "pointer",
              whiteSpace: "nowrap",
              maxWidth: "120px",
              width: "120px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.7)",
            }}
            title={sprint}
          >
            {sprint}
          </Box>
        );
      },
    },

    {
      name: "Assignee",
      align: "center",
      score: (row) => row.assignee?.toLowerCase() || "", // <-- This makes it sortable
      render: (row) =>
        row.assigneeAvatar ? (
          <Box
            sx={{
              alignItems: "center",
              gap: 1,
              display: "inline-flex",
              cursor: "pointer",
              width: "140px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            onClick={() => openUserModal(row.assignee)}
            title={row.assignee}
          >
            <img
              src={row.assigneeAvatar}
              alt={row.assignee}
              title={row.assignee}
              style={{ borderRadius: "50%", height: "32px" }}
            />
            <Typography noWrap sx={{ fontSize: "13px", color: theme.palette.text.primary }}>
              {row.assignee}
            </Typography>
          </Box>
        ) : (
          "N/A"
        ),
    },
    {
      name: "Progress",
      align: "left",
      score: (row) => row.percentComplete || 0, // 👈 Add this to make it sortable
      render: (row) => {
        const percent = row.percentComplete || 0;

        const getColor = (value) => {
          if (value < 40) return "#f44336"; // red
          if (value < 70) return "#ff9800"; // orange
          return "#4caf50"; // green
        };

        const barColor = getColor(percent);

        return (
          <Box sx={{ width: "100%", minWidth: 120 }}>
            <Box
              sx={{
                position: "relative",
                height: 16,
                borderRadius: 5,
                backgroundColor: theme.palette.background.subBox,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${percent}%`,
                  height: "100%",
                  backgroundColor: barColor,
                  transition: "width 0.3s ease",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: theme.palette.text.primary,
                  fontSize: "11px",
                  fontWeight: "bold",
                  lineHeight: "16px",
                  textShadow: theme.palette.text.shadow,
                }}
              >
                {percent}%
              </Typography>
            </Box>
          </Box>
        );
      },
    },
  ];

  // Show loading state until critical data is loaded
  if (isInitialLoading) {
    return (
      <Box
        sx={{
          height: "calc(100vh - 60px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          backgroundColor: "background.default",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "80px",
            height: "80px",
            animation: "float 2s ease-in-out infinite",
            "@keyframes float": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-15px)" },
            },
          }}
        >
          {/* Ticket body */}
          <Box
            sx={{
              position: "absolute",
              width: "70px",
              height: "50px",
              backgroundColor: "primary.main",
              borderRadius: "8px",
              top: "15px",
              left: "5px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              "&::before": {
                content: '""',
                position: "absolute",
                width: "50px",
                height: "3px",
                backgroundColor: "background.paper",
                top: "12px",
                left: "10px",
                borderRadius: "2px",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                width: "40px",
                height: "3px",
                backgroundColor: "background.paper",
                top: "22px",
                left: "10px",
                borderRadius: "2px",
              },
            }}
          />
          {/* Checkmark */}
          <Box
            sx={{
              position: "absolute",
              width: "25px",
              height: "25px",
              borderRadius: "50%",
              backgroundColor: "success.main",
              top: "0",
              right: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "pulse 1.5s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": { transform: "scale(1)", opacity: 1 },
                "50%": { transform: "scale(1.1)", opacity: 0.8 },
              },
              "&::before": {
                content: '"✓"',
                color: "#fff",
                fontSize: "16px",
                fontWeight: "bold",
              },
            }}
          />
          {/* Shadow */}
          <Box
            sx={{
              position: "absolute",
              bottom: "-15px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "60px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: "action.hover",
              filter: "blur(4px)",
              animation: "shadowMove 2s ease-in-out infinite",
              "@keyframes shadowMove": {
                "0%, 100%": { transform: "translateX(-50%) scale(1)", opacity: 0.3 },
                "50%": { transform: "translateX(-50%) scale(0.8)", opacity: 0.5 },
              },
            }}
          />
        </Box>
        <Typography variant="h6" color="text.primary">
          Loading Jira Analytics...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        marginLeft: "5px",
      }}
    >
      <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
        <Box
          title="CloudOps Jira"
          sx={{
            height: "calc(100vh - 60px)",
            backgroundColor: "background.default",
          }}
        >
          <Grid
            container
            sx={{
              height: "100%",
              paddingTop: "0px",
              paddingLeft: "20px",
              paddingRight: "20px",
              paddingTop: "40px",
              overflow: "hidden",
              flexDirection: "row",
            }}
            spacing={2}
          >
            <Grid item xs={12} height={"50%"}>
              <Grid container sx={{ height: "100%" }} columnSpacing={2} rowSpacing={2}>
                <Grid item xs={12} sx={{ height: "100%" }}>
                  <GenericTable
                    data={jiraTickets}
                    loading={loadingJiraTickets}
                    sortConfig={sortConfig}
                    setSortConfig={setSortConfig}
                    defaultSort={sortConfig}
                    alternateRowColor={false}
                    onActionClick={(row) => {
                      setSelectedTicket(row.id ? row : null);
                    }}
                    headerColor="#7E4553"
                    title="Active Jira Tickets"
                    defaultColumns={["boardId", "department", "ticketKey", "summary"]}
                    columnNameMapping={{
                      boardId: "Board",
                      epic: "Epic",
                      ticketKey: "Ticket",
                      summary: "Title",
                      status: "Status",
                    }}
                    maxColumnWidths={{
                      summary: "300px",
                    }}
                    showSearch
                    customColumns={customColumns}
                    showActionColumn
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} height={"50%"}>
              <Grid container sx={{ height: "100%" }} columnSpacing={2} rowSpacing={2}>
                <Grid item xs={4} sx={{ height: "100%" }}>
                  <JiraProductivity
                    title={"Workload Insights"}
                    loading={loadingProductivity}
                    data={assigndData}
                    userClicked={(assigneeName) => openUserModal(assigneeName)}
                  />
                </Grid>

                <Grid item xs={8} sx={{ height: "100%" }}>
                  <JiraPerformanceTrend
                    data={teamTrendData}
                    selectedMetric={teamMetric}
                    onMetricChange={(metric) => setTeamMetric(metric)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <JiraUserDetailsModal
            open={userModalOpen}
            onClose={() => setUserModalOpen(false)}
            user={selectedUser}
          ></JiraUserDetailsModal>

          <SprintTicketsModal
            open={sprintModalOpen}
            onClose={() => setSprintModalOpen(false)}
            sprintName={selectedSprintTickets[0]?.sprintName}
            tickets={selectedSprintTickets}
          />

          <JiraTicketDetailsModal
            open={!!selectedTicket}
            ticket={selectedTicket}
            onClose={() => setSelectedTicket(null)}
          />
        </Box>

        <Box title="Sprint Health">
          <Box
            sx={{
              height: "calc(100vh - 80px)",

              backgroundColor: "background.default",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              paddingLeft: "20px",
              paddingRight: "20px",
              paddingTop: "20px",
            }}
          >
            <Box sx={{ overflowY: "auto", flex: 1 }}>
              {/* Sprint Health KPIs */}
              {sprintHealth && sprintHealth.length > 0 && (
                <Grid container spacing={2}>
                  {(sprintHealth || []).map((sprint, idx) => (
                    <Grid item xs={12} key={idx}>
                      <Box
                        sx={{
                          p: 3,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={8}>
                            <Box display="flex" alignItems="center" gap={2} mb={2}>
                              <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                                {sprint.sprintName}
                              </Typography>
                              <Chip
                                label={sprint.status}
                                size="small"
                                icon={
                                  sprint.onTrack ? (
                                    <CheckCircleIcon />
                                  ) : sprint.status === "At Risk" ? (
                                    <ErrorIcon />
                                  ) : (
                                    <WarningAmberIcon />
                                  )
                                }
                                color={
                                  sprint.onTrack
                                    ? "success"
                                    : sprint.status === "At Risk"
                                      ? "error"
                                      : "warning"
                                }
                              />
                              <Chip label={sprint.team} size="small" />
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ color: theme.palette.text.secondary, mb: 1 }}
                            >
                              {sprint.daysRemaining} days remaining • {sprint.percentTimeElapsed}%
                              time elapsed
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              <Box display="flex" justifyContent="space-between" mb={0.5}>
                                <Typography variant="caption">Progress</Typography>
                                <Typography variant="caption" fontWeight="bold">
                                  {sprint.percentComplete}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={sprint.percentComplete}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: theme.palette.background.subBox,
                                  "& .MuiLinearProgress-bar": {
                                    backgroundColor: sprint.onTrack
                                      ? "#4caf50"
                                      : sprint.status === "At Risk"
                                        ? "#f44336"
                                        : "#ff9800",
                                  },
                                }}
                              />
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={3}>
                                <Typography variant="caption" color="text.secondary">
                                  Done
                                </Typography>
                                <Typography variant="h6" color="#4caf50">
                                  {sprint.doneTickets}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="caption" color="text.secondary">
                                  In Progress
                                </Typography>
                                <Typography variant="h6" color="#ff9800">
                                  {sprint.inProgressTickets}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="caption" color="text.secondary">
                                  To Do
                                </Typography>
                                <Typography variant="h6" color="#2196f3">
                                  {sprint.todoTickets}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="caption" color="text.secondary">
                                  Total
                                </Typography>
                                <Typography variant="h6">{sprint.totalTickets}</Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box
                              sx={{
                                p: 2,
                                backgroundColor: theme.palette.background.default,
                                borderRadius: 1,
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" gutterBottom>
                                Story Points
                              </Typography>
                              <Typography variant="h5" gutterBottom>
                                {sprint.completedPoints} / {sprint.totalPoints}
                              </Typography>
                              {sprint.scopeChangeCount > 0 && (
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                  {sprint.scopeChangeCount} items added mid-sprint
                                </Alert>
                              )}
                              {(sprint?.atRiskItems?.length ?? 0) > 0 && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                  {sprint?.atRiskItems?.length ?? 0} items at risk
                                </Alert>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                        {(sprint?.atRiskItems?.length ?? 0) > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="error" gutterBottom>
                              At Risk Items:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                              {(sprint?.atRiskItems || []).map((item) => (
                                <Chip
                                  key={item.ticketKey}
                                  label={`${item.ticketKey} (${item.ageInDays}d)`}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Box>
        </Box>

        <Box title="Quality & SLA">
          <Box
            sx={{
              height: "calc(100vh - 60px)",
              backgroundColor: "background.default",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              paddingLeft: "20px",
              paddingRight: "20px",
              paddingTop: "20px",
            }}
          >
            {/* KPI Cards - All in one row */}
            {(slaCompliance || qualityMetrics) && (
              <Box sx={{ marginBottom: "40px", flexShrink: 0 }}>
                <Grid container spacing={2}>
                  {/* SLA KPIs */}
                  {slaCompliance && (
                    <>
                      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            height: "140px",
                            width: "100%",
                            background: "linear-gradient(135deg, #4caf5015 0%, #4caf5005 100%)",
                            border: "1px solid #4caf5030",
                            borderRadius: 2,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 20px #4caf5040",
                              border: "1px solid #4caf5060",
                            },
                          }}
                        >
                          <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                fontWeight={600}
                                sx={{ textTransform: "uppercase" }}
                              >
                                Overall Compliance
                              </Typography>
                              <Tooltip title="Compared to previous period" placement="top" arrow>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    backgroundColor:
                                      (slaCompliance?.overallCompliance ?? 0) >= 90
                                        ? "#4caf5020"
                                        : "#f4433620",
                                    cursor: "help",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    fontWeight={700}
                                    sx={{
                                      color:
                                        (slaCompliance?.overallCompliance ?? 0) >= 90
                                          ? "#4caf50"
                                          : "#f44336",
                                    }}
                                  >
                                    {(slaCompliance?.overallCompliance ?? 0) >= 90 ? "↑" : "↓"}{" "}
                                    {(slaCompliance?.overallCompliance ?? 0) >= 90 ? "5.2" : "2.1"}%
                                  </Typography>
                                </Box>
                              </Tooltip>
                            </Box>
                            <Typography variant="h4" fontWeight={700} sx={{ color: "#4caf50" }}>
                              {slaCompliance?.overallCompliance ?? 0}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Average SLA compliance rate
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            height: "140px",
                            width: "100%",
                            background: "linear-gradient(135deg, #f4433615 0%, #f4433605 100%)",
                            border: "1px solid #f4433630",
                            borderRadius: 2,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 20px #f4433640",
                              border: "1px solid #f4433660",
                            },
                          }}
                        >
                          <Box display="flex" flexDirection="column" gap={1}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={600}
                              sx={{ textTransform: "uppercase" }}
                            >
                              Total Breaches
                            </Typography>
                            <Typography variant="h4" fontWeight={700} sx={{ color: "#f44336" }}>
                              {slaCompliance?.breachedItems?.length ?? 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Tickets that exceeded SLA
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    </>
                  )}

                  {/* Quality Metrics KPIs */}
                  {qualityMetrics && (
                    <>
                      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            height: "140px",
                            width: "100%",
                            background: "linear-gradient(135deg, #f4433615 0%, #f4433605 100%)",
                            border: "1px solid #f4433630",
                            borderRadius: 2,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 20px #f4433640",
                              border: "1px solid #f4433660",
                            },
                          }}
                        >
                          <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                fontWeight={600}
                                sx={{ textTransform: "uppercase" }}
                              >
                                Bugs Created (30d)
                              </Typography>
                              <Tooltip title="Compared to previous period" placement="top" arrow>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    backgroundColor:
                                      qualityMetrics?.summary?.bugCreationTrend < 0
                                        ? "#4caf5020"
                                        : "#f4433620",
                                    cursor: "help",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    fontWeight={700}
                                    sx={{
                                      color:
                                        qualityMetrics?.summary?.bugCreationTrend < 0
                                          ? "#4caf50"
                                          : "#f44336",
                                    }}
                                  >
                                    {qualityMetrics?.summary?.bugCreationTrend > 0 ? "↑" : "↓"}{" "}
                                    {Math.abs(qualityMetrics?.summary?.bugCreationTrend).toFixed(1)}
                                    %
                                  </Typography>
                                </Box>
                              </Tooltip>
                            </Box>
                            <Typography variant="h4" fontWeight={700} sx={{ color: "#f44336" }}>
                              {qualityMetrics?.summary?.bugsCreatedLast30Days}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              New bugs reported
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            height: "140px",
                            width: "100%",
                            background: "linear-gradient(135deg, #4caf5015 0%, #4caf5005 100%)",
                            border: "1px solid #4caf5030",
                            borderRadius: 2,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 20px #4caf5040",
                              border: "1px solid #4caf5060",
                            },
                          }}
                        >
                          <Box display="flex" flexDirection="column" gap={1}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={600}
                              sx={{ textTransform: "uppercase" }}
                            >
                              Bugs Resolved (30d)
                            </Typography>
                            <Typography variant="h4" fontWeight={700} sx={{ color: "#4caf50" }}>
                              {qualityMetrics?.summary?.bugsResolvedLast30Days}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Bugs closed
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            )}

            {/* Charts - Fill remaining space */}
            {qualityMetrics && (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Grid container spacing={2} sx={{ height: "100%" }}>
                  <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                    <LineGraph
                      title="Weekly Bug Trend"
                      description="Bugs created vs resolved"
                      data={{
                        Created: (qualityMetrics?.weeklyTrend || []).map((w) => ({
                          month: w.week,
                          value: w.created,
                        })),
                        Resolved: (qualityMetrics?.weeklyTrend || []).map((w) => ({
                          month: w.week,
                          value: w.resolved,
                        })),
                      }}
                      defaultProvider="ALL"
                      showAll={true}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                    <BarChartGeneric
                      title="Bug Aging Distribution"
                      data={{
                        title: "Bug Aging",
                        metrics: {
                          ALL: [
                            { name: "Fresh (≤7d)", value: qualityMetrics?.agingBuckets?.fresh },
                            { name: "Week (7-30d)", value: qualityMetrics?.agingBuckets?.week },
                            { name: "Month (30-90d)", value: qualityMetrics?.agingBuckets?.month },
                            { name: "Old (>90d)", value: qualityMetrics?.agingBuckets?.old },
                          ],
                        },
                      }}
                      loading={loadingQualityMetrics}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Box>

        <Box title="Bottlenecks & Capacity">
          <Box
            sx={{
              height: "calc(100vh - 60px)",
              backgroundColor: "background.default",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              paddingLeft: "20px",
              paddingRight: "20px",
              paddingTop: "20px",
            }}
          >
            {/* KPIs - Top section */}
            {capacityForecast && (
              <Box sx={{ marginBottom: "40px", flexShrink: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: "140px",
                        width: "100%",
                        background: "linear-gradient(135deg, #00bcd415 0%, #00bcd405 100%)",
                        border: "1px solid #00bcd430",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 20px #00bcd440",
                          border: "1px solid #00bcd460",
                        },
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ textTransform: "uppercase" }}
                        >
                          Avg Velocity
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: "#00bcd4" }}>
                          {capacityForecast?.historicalVelocity?.avgPoints}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Average story points per sprint
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: "140px",
                        width: "100%",
                        background: "linear-gradient(135deg, #e91e6315 0%, #e91e6305 100%)",
                        border: "1px solid #e91e6330",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 20px #e91e6340",
                          border: "1px solid #e91e6360",
                        },
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ textTransform: "uppercase" }}
                        >
                          Backlog Items
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: "#e91e63" }}>
                          {capacityForecast?.backlog?.totalItems}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total items in backlog
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: "140px",
                        width: "100%",
                        background: "linear-gradient(135deg, #9c27b015 0%, #9c27b005 100%)",
                        border: "1px solid #9c27b030",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 20px #9c27b040",
                          border: "1px solid #9c27b060",
                        },
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ textTransform: "uppercase" }}
                        >
                          Backlog Points
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: "#9c27b0" }}>
                          {capacityForecast?.backlog?.totalPoints}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total story points
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: "140px",
                        width: "100%",
                        background: "linear-gradient(135deg, #ff980015 0%, #ff980005 100%)",
                        border: "1px solid #ff980030",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 20px #ff980040",
                          border: "1px solid #ff980060",
                        },
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ textTransform: "uppercase" }}
                        >
                          Est. Sprints
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: "#ff9800" }}>
                          {capacityForecast?.backlog?.estimatedSprintsToComplete}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          To complete backlog
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Charts - Fill remaining space */}
            {(capacityForecast || bottleneckAnalysis) && (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Grid container spacing={2} sx={{ height: "100%" }}>
                  {/* Top row: Capacity Forecast and Historical Velocity side by side */}
                  {capacityForecast && (
                    <>
                      <Grid item xs={12} md={6} sx={{ height: "50%" }}>
                        <LineGraph
                          title="Capacity Forecast"
                          description="Conservative, moderate, and aggressive estimates"
                          data={{
                            Conservative: (capacityForecast?.forecast || []).map((f) => ({
                              month: f.sprint,
                              value: f.conservative,
                            })),
                            Moderate: (capacityForecast?.forecast || []).map((f) => ({
                              month: f.sprint,
                              value: f.moderate,
                            })),
                            Aggressive: (capacityForecast?.forecast || []).map((f) => ({
                              month: f.sprint,
                              value: f.aggressive,
                            })),
                          }}
                          defaultProvider="ALL"
                          showAll={true}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} sx={{ height: "50%" }}>
                        <LineGraph
                          title="Historical Velocity Trend"
                          description="Story points completed per sprint"
                          data={{
                            Velocity: (capacityForecast?.historicalVelocity?.trend || []).map(
                              (s) => ({
                                month: s.sprint,
                                value: s.points,
                              })
                            ),
                          }}
                          defaultProvider="Velocity"
                        />
                      </Grid>
                    </>
                  )}

                  {/* Bottom row: Cycle Time Analysis full width */}
                  {bottleneckAnalysis && (
                    <Grid item xs={12} sx={{ height: "50%", marginTop: "16px" }}>
                      <BarChartGeneric
                        title="Cycle Time by Issue Type"
                        data={{
                          title: "Cycle Time Analysis",
                          metrics: {
                            ALL: (bottleneckAnalysis?.byIssueType || []).map((item) => ({
                              name: item.issueType,
                              value: item.avgCycleTime,
                            })),
                          },
                        }}
                        loading={loadingBottleneckAnalysis}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </Box>
        </Box>

        <Box title="Workload & Balance">
          <Box
            sx={{
              height: "calc(100vh - 60px)",
              backgroundColor: "background.default",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                paddingLeft: "20px",
                paddingRight: "20px",
                paddingTop: "20px",
                overflowY: "auto",
                flex: 1,
              }}
            >
              {workloadBalance && (
                <>
                  <Box sx={{ marginBottom: "40px", flexShrink: 0 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            height: "140px",
                            width: "100%",
                            background:
                              workloadBalance?.summary?.balanceStatus === "Balanced"
                                ? "linear-gradient(135deg, #4caf5015 0%, #4caf5005 100%)"
                                : "linear-gradient(135deg, #ff980015 0%, #ff980005 100%)",
                            border:
                              workloadBalance?.summary?.balanceStatus === "Balanced"
                                ? "1px solid #4caf5030"
                                : "1px solid #ff980030",
                            borderRadius: 2,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow:
                                workloadBalance?.summary?.balanceStatus === "Balanced"
                                  ? "0 4px 20px #4caf5040"
                                  : "0 4px 20px #ff980040",
                              border:
                                workloadBalance?.summary?.balanceStatus === "Balanced"
                                  ? "1px solid #4caf5060"
                                  : "1px solid #ff980060",
                            },
                          }}
                        >
                          <Box display="flex" flexDirection="column" gap={1}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={600}
                              sx={{ textTransform: "uppercase" }}
                            >
                              Balance Status
                            </Typography>
                            <Typography
                              variant="h4"
                              fontWeight={700}
                              sx={{
                                color:
                                  workloadBalance?.summary?.balanceStatus === "Balanced"
                                    ? "#4caf50"
                                    : "#ff9800",
                              }}
                            >
                              {workloadBalance?.summary?.balanceStatus}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Fairness Score: {workloadBalance?.summary?.fairnessScore}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            height: "140px",
                            width: "100%",
                            background: "linear-gradient(135deg, #2196f315 0%, #2196f305 100%)",
                            border: "1px solid #2196f330",
                            borderRadius: 2,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 20px #2196f340",
                              border: "1px solid #2196f360",
                            },
                          }}
                        >
                          <Box display="flex" flexDirection="column" gap={1}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={600}
                              sx={{ textTransform: "uppercase" }}
                            >
                              Avg Tickets/Person
                            </Typography>
                            <Typography variant="h4" fontWeight={700} sx={{ color: "#2196f3" }}>
                              {workloadBalance?.summary?.avgTicketsPerPerson}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Current workload average
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            height: "140px",
                            width: "100%",
                            background: "linear-gradient(135deg, #f4433615 0%, #f4433605 100%)",
                            border: "1px solid #f4433630",
                            borderRadius: 2,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 20px #f4433640",
                              border: "1px solid #f4433660",
                            },
                          }}
                        >
                          <Box display="flex" flexDirection="column" gap={1}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={600}
                              sx={{ textTransform: "uppercase" }}
                            >
                              Overloaded
                            </Typography>
                            <Typography variant="h4" fontWeight={700} sx={{ color: "#f44336" }}>
                              {workloadBalance?.summary?.overloadedCount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Team members with high load
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            height: "140px",
                            width: "100%",
                            background: "linear-gradient(135deg, #4caf5015 0%, #4caf5005 100%)",
                            border: "1px solid #4caf5030",
                            borderRadius: 2,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 20px #4caf5040",
                              border: "1px solid #4caf5060",
                            },
                          }}
                        >
                          <Box display="flex" flexDirection="column" gap={1}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={600}
                              sx={{ textTransform: "uppercase" }}
                            >
                              Balanced
                            </Typography>
                            <Typography variant="h4" fontWeight={700} sx={{ color: "#4caf50" }}>
                              {workloadBalance?.summary?.balancedCount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Team members well balanced
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 3,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          Team Load Distribution
                        </Typography>
                        <Grid container spacing={2}>
                          {(workloadBalance?.workload || []).slice(0, 8).map((member, idx) => {
                            const isOverloaded =
                              member.activeTickets >
                              (workloadBalance?.summary?.avgTicketsPerPerson || 0) * 1.5;
                            const isUnder =
                              member.activeTickets <
                              (workloadBalance?.summary?.avgTicketsPerPerson || 0) * 0.5;
                            return (
                              <Grid item xs={12} sm={6} md={3} key={idx}>
                                <Box
                                  sx={{
                                    p: 2,
                                    border: `2px solid ${isOverloaded ? "#f44336" : isUnder ? "#2196f3" : "#4caf50"}`,
                                    borderRadius: 2,
                                    backgroundColor: isOverloaded
                                      ? "#f4433610"
                                      : isUnder
                                        ? "#2196f310"
                                        : "#4caf5010",
                                  }}
                                >
                                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    {member.avatar && (
                                      <img
                                        src={member.avatar}
                                        alt={member.assignee}
                                        style={{
                                          borderRadius: "50%",
                                          height: "32px",
                                          width: "32px",
                                        }}
                                      />
                                    )}
                                    <Typography variant="body2" fontWeight={600}>
                                      {member.assignee}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="h4"
                                    color={
                                      isOverloaded ? "#f44336" : isUnder ? "#2196f3" : "#4caf50"
                                    }
                                  >
                                    {member.activeTickets}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {member.totalPoints} pts • {member.avgTicketAge}d avg
                                  </Typography>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          </Box>
        </Box>

        <Box title="Critical Items">
          <Box
            sx={{
              height: "calc(100vh - 60px)",
              backgroundColor: "background.default",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                paddingLeft: "20px",
                paddingRight: "20px",
                paddingTop: "40px",
                overflowY: "auto",
                flex: 1,
              }}
            >
              {/* Blocked Items */}
              {blockedItems && (
                <Box sx={{ marginBottom: "40px", flexShrink: 0 }}>
                  <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
                    <BlockIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                    Blocked Items
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          height: "140px",
                          width: "100%",
                          background: "linear-gradient(135deg, #f4433615 0%, #f4433605 100%)",
                          border: "1px solid #f4433630",
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 20px #f4433640",
                            border: "1px solid #f4433660",
                          },
                        }}
                      >
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textTransform: "uppercase" }}
                          >
                            Total Blocked
                          </Typography>
                          <Typography variant="h4" fontWeight={700} sx={{ color: "#f44336" }}>
                            {blockedItems?.summary?.totalBlocked}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Items currently blocked
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          height: "140px",
                          width: "100%",
                          background: "linear-gradient(135deg, #f4433615 0%, #f4433605 100%)",
                          border: "1px solid #f4433630",
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 20px #f4433640",
                            border: "1px solid #f4433660",
                          },
                        }}
                      >
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textTransform: "uppercase" }}
                          >
                            Critical Blocked
                          </Typography>
                          <Typography variant="h4" fontWeight={700} sx={{ color: "#f44336" }}>
                            {blockedItems?.summary?.criticalBlocked}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            High/Highest priority blocked
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          height: "140px",
                          width: "100%",
                          background: "linear-gradient(135deg, #ff980015 0%, #ff980005 100%)",
                          border: "1px solid #ff980030",
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 20px #ff980040",
                            border: "1px solid #ff980060",
                          },
                        }}
                      >
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textTransform: "uppercase" }}
                          >
                            Avg Days Blocked
                          </Typography>
                          <Typography variant="h4" fontWeight={700} sx={{ color: "#ff9800" }}>
                            {blockedItems?.summary?.avgDaysBlocked}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Average blocking duration (days)
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          height: "140px",
                          width: "100%",
                          background: "linear-gradient(135deg, #9c27b015 0%, #9c27b005 100%)",
                          border: "1px solid #9c27b030",
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 20px #9c27b040",
                            border: "1px solid #9c27b060",
                          },
                        }}
                      >
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textTransform: "uppercase" }}
                          >
                            Longest Blocked
                          </Typography>
                          <Typography variant="h4" fontWeight={700} sx={{ color: "#9c27b0" }}>
                            {blockedItems?.summary?.longestBlocked}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Maximum blocking duration (days)
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
              {blockedItems && blockedItems.items && blockedItems.items.length > 0 && (
                <Grid container spacing={2} sx={{ marginBottom: "40px" }}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="h6" gutterBottom color="error">
                        Top 5 Blocked Items Requiring Attention
                      </Typography>
                      <Grid container spacing={2}>
                        {(blockedItems?.items || []).slice(0, 5).map((item, idx) => (
                          <Grid item xs={12} key={idx}>
                            <Box
                              sx={{
                                p: 2,
                                backgroundColor: theme.palette.background.default,
                                borderRadius: 1,
                                borderLeft: `4px solid #f44336`,
                              }}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Box>
                                  <Typography variant="body1" fontWeight={600}>
                                    {item.ticketKey} - {item.summary}
                                  </Typography>
                                  <Box display="flex" gap={1} mt={1}>
                                    <Chip label={item.priority} size="small" color="error" />
                                    <Chip label={item.issueType || item.status} size="small" />
                                    <Chip label={item.assignee} size="small" variant="outlined" />
                                  </Box>
                                </Box>
                                <Box textAlign="right">
                                  <Typography variant="h4" color="error">
                                    {item.daysBlocked}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    days blocked
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              )}
              {/* Critical Aging Items */}
              {criticalAging && (
                <Box sx={{ marginBottom: "40px", flexShrink: 0 }}>
                  <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
                    <WarningAmberIcon sx={{ mr: 1, verticalAlign: "middle", color: "#ff9800" }} />
                    Critical Aging Issues
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          height: "140px",
                          width: "100%",
                          background: "linear-gradient(135deg, #ff980015 0%, #ff980005 100%)",
                          border: "1px solid #ff980030",
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 20px #ff980040",
                            border: "1px solid #ff980060",
                          },
                        }}
                      >
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textTransform: "uppercase" }}
                          >
                            Total Critical
                          </Typography>
                          <Typography variant="h4" fontWeight={700} sx={{ color: "#ff9800" }}>
                            {criticalAging?.summary?.totalCritical}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            High priority or aging items
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          height: "140px",
                          width: "100%",
                          background: "linear-gradient(135deg, #ff980015 0%, #ff980005 100%)",
                          border: "1px solid #ff980030",
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 20px #ff980040",
                            border: "1px solid #ff980060",
                          },
                        }}
                      >
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textTransform: "uppercase" }}
                          >
                            High Priority Aging
                          </Typography>
                          <Typography variant="h4" fontWeight={700} sx={{ color: "#ff9800" }}>
                            {criticalAging?.summary?.highPriorityAging}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Critical items over 7 days
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          height: "140px",
                          width: "100%",
                          background: "linear-gradient(135deg, #f4433615 0%, #f4433605 100%)",
                          border: "1px solid #f4433630",
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 20px #f4433640",
                            border: "1px solid #f4433660",
                          },
                        }}
                      >
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textTransform: "uppercase" }}
                          >
                            Aging Bugs
                          </Typography>
                          <Typography variant="h4" fontWeight={700} sx={{ color: "#f44336" }}>
                            {criticalAging?.summary?.agingBugs}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Bugs over 7 days old
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          height: "140px",
                          width: "100%",
                          background: "linear-gradient(135deg, #f4433615 0%, #f4433605 100%)",
                          border: "1px solid #f4433630",
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 20px #f4433640",
                            border: "1px solid #f4433660",
                          },
                        }}
                      >
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textTransform: "uppercase" }}
                          >
                            Open Incidents
                          </Typography>
                          <Typography variant="h4" fontWeight={700} sx={{ color: "#f44336" }}>
                            {criticalAging?.summary?.openIncidents}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Active incident tickets
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
              {criticalAging && criticalAging.items && criticalAging.items.length > 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        Top Critical Items Requiring Immediate Attention
                      </Typography>
                      <Grid container spacing={2}>
                        {(criticalAging?.items || []).slice(0, 6).map((item, idx) => (
                          <Grid item xs={12} sm={6} md={4} key={idx}>
                            <Box
                              sx={{
                                p: 2,
                                backgroundColor: theme.palette.background.default,
                                borderRadius: 1,
                                borderLeft: `4px solid ${item.priority === "Highest" || item.priority === "High" ? "#f44336" : "#ff9800"}`,
                                height: "100%",
                              }}
                            >
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {item.ticketKey}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  mb: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.summary}
                              </Typography>
                              <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                                <Chip
                                  label={item.priority}
                                  size="small"
                                  sx={{ fontSize: "0.7rem" }}
                                />
                                <Chip
                                  label={item.issueType}
                                  size="small"
                                  sx={{ fontSize: "0.7rem" }}
                                  variant="outlined"
                                />
                              </Box>
                              <Typography variant="h5" color={item.age > 14 ? "error" : "warning"}>
                                {item.age} days
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Box>
        </Box>

        <Box title="Team Performance">
          <Box
            sx={{
              height: "calc(100vh - 60px)",
              backgroundColor: "background.default",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              paddingLeft: "20px",
              paddingRight: "20px",
              paddingTop: "40px",
            }}
          >
            {/* KPIs - Top section */}
            {teamComparison && (
              <Box sx={{ marginBottom: "40px", flexShrink: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: "140px",
                        width: "100%",
                        background: "linear-gradient(135deg, #2196f315 0%, #2196f305 100%)",
                        border: "1px solid #2196f330",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 20px #2196f340",
                          border: "1px solid #2196f360",
                        },
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ textTransform: "uppercase" }}
                        >
                          Avg Cycle Time
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: "#2196f3" }}>
                          {teamComparison?.averages?.avgCycleTime}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Cross-team average (days)
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: "140px",
                        width: "100%",
                        background: "linear-gradient(135deg, #4caf5015 0%, #4caf5005 100%)",
                        border: "1px solid #4caf5030",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 20px #4caf5040",
                          border: "1px solid #4caf5060",
                        },
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ textTransform: "uppercase" }}
                        >
                          Avg Velocity
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: "#4caf50" }}>
                          {teamComparison?.averages?.avgVelocity}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Cross-team average (pts)
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: "140px",
                        width: "100%",
                        background: "linear-gradient(135deg, #ff980015 0%, #ff980005 100%)",
                        border: "1px solid #ff980030",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 20px #ff980040",
                          border: "1px solid #ff980060",
                        },
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ textTransform: "uppercase" }}
                        >
                          Avg Bug Rate
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: "#ff9800" }}>
                          {teamComparison?.averages?.avgBugRate}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Cross-team average
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: "140px",
                        width: "100%",
                        background: "linear-gradient(135deg, #9c27b015 0%, #9c27b005 100%)",
                        border: "1px solid #9c27b030",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 20px #9c27b040",
                          border: "1px solid #9c27b060",
                        },
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ textTransform: "uppercase" }}
                        >
                          Teams Tracked
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: "#9c27b0" }}>
                          {teamComparison?.teams?.length ?? 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Active development teams
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Charts and content - Fill remaining space */}
            {(teamComparison || weeklySummary) && (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Grid container spacing={2} sx={{ height: "100%" }}>
                  {/* Velocity chart - top half */}
                  {teamComparison && (
                    <Grid item xs={12} sx={{ height: "50%" }}>
                      <BarChartGeneric
                        title="Team Velocity Comparison"
                        data={{
                          title: "Velocity by Team",
                          metrics: {
                            ALL: (teamComparison?.teams || []).map((t) => ({
                              name: t.team,
                              value: t.metrics.velocity,
                            })),
                          },
                        }}
                        loading={loadingTeamComparison}
                      />
                    </Grid>
                  )}

                  {/* Weekly Summary - bottom half */}
                  {weeklySummary && weeklySummary.length > 0 && (
                    <Grid item xs={12} sx={{ height: "50%", marginTop: "16px", overflow: "auto" }}>
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          Weekly Performance Snapshot
                        </Typography>
                        <Grid container spacing={2}>
                          {(weeklySummary || []).slice(0, 4).map((week, idx) => (
                            <Grid item xs={12} sm={6} md={3} key={idx}>
                              <Box
                                sx={{
                                  p: 2,
                                  backgroundColor: theme.palette.background.default,
                                  borderRadius: 1,
                                  borderTop: `3px solid #4caf50`,
                                }}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  {week.Team}
                                </Typography>
                                <Box mt={2}>
                                  <Typography variant="caption" color="text.secondary">
                                    New Tickets
                                  </Typography>
                                  <Typography variant="h5" color="#2196f3">
                                    {week.NewTickets}
                                  </Typography>
                                </Box>
                                <Box mt={1}>
                                  <Typography variant="caption" color="text.secondary">
                                    Resolved
                                  </Typography>
                                  <Typography variant="h5" color="#4caf50">
                                    {week.ResolvedTickets}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </Box>
        </Box>
      </Tabs>
      <JiraViweSideBar
        boards={boards}
        onDepartmentChange={(dept) => {
          setSelectedDepartment(dept || "ALL");
        }}
      ></JiraViweSideBar>
    </Box>
  );
};

export default JiraView;
